import { Select, SimpleGrid, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import UploadFile from 'components/files/UploadFile';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';

export interface HrPersonFileCreateFormValues {
  fileId: string;
  fileName: string;
  mimeType: string;
  categoryId: string | null;
}

export interface HrPersonFileEditFormValues {
  displayName: string;
  categoryId: string;
}

interface HrPersonFileFormProps {
  mode: 'create' | 'edit';
  createForm: UseFormReturnType<HrPersonFileCreateFormValues>;
  editForm: UseFormReturnType<HrPersonFileEditFormValues>;
  categories: any[];
  isTablet: boolean;
  onCancel: () => void;
  onCreate: () => void;
  onEditSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
}

/** Dedicated create/edit form for one HR person-file association. */
export default function HrPersonFileForm({
  mode,
  createForm,
  editForm,
  categories,
  isTablet,
  onCancel,
  onCreate,
  onEditSubmit,
}: HrPersonFileFormProps) {
  if (mode === 'create') {
    return (
      <FormWrapper
        primaryButtonText="Vytvoriť"
        primaryIcon={<IconPlus stroke={1.5} />}
        secondaryButtonText="Zrušiť"
        onSecondaryClick={onCancel}
        hidePrimaryButton={!createForm.values.fileId || !createForm.values.categoryId}
        skipSecondaryButtonConfirm={!createForm.isDirty()}
        onSubmit={(event) => {
          event.preventDefault();
          onCreate();
        }}
        loading={false}
      >
        <FormInputGroup groupTitle="Súbor">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <UploadFile
              label="Nahrať súbor"
              value={createForm.values.fileId}
              onChange={(value) => createForm.setFieldValue('fileId', value)}
              onFileProcessed={(file) => {
                createForm.setFieldValue('fileName', file?.name ?? '');
                createForm.setFieldValue('mimeType', file?.type ?? '');
              }}
            />
            <Select
              size="lg"
              label="Kategória"
              withAsterisk
              data={categories.map((category) => ({
                value: String(category.categoryId),
                label: category.name ?? `Kategória ${category.categoryId}`,
              }))}
              value={createForm.values.categoryId}
              onChange={(value) => createForm.setFieldValue('categoryId', value)}
            />
          </SimpleGrid>
        </FormInputGroup>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper
      primaryButtonText="Uložiť"
      primaryIcon={<IconCheck stroke={1.5} />}
      secondaryButtonText="Zrušiť"
      onSecondaryClick={onCancel}
      skipSecondaryButtonConfirm={!editForm.isDirty()}
      onSubmit={onEditSubmit}
      loading={false}
    >
      <FormInputGroup groupTitle="Súbor">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput size="lg" label="Názov súboru" withAsterisk {...editForm.getInputProps('displayName')} />
          <Select
            size="lg"
            label="Kategória súboru"
            withAsterisk
            data={categories.map((category) => ({
              value: String(category.categoryId),
              label: category.name ?? `Kategória ${category.categoryId}`,
            }))}
            {...editForm.getInputProps('categoryId')}
          />
        </SimpleGrid>
      </FormInputGroup>
    </FormWrapper>
  );
}
